/** External connector segments used in `/data-sources/:id/external/:connector`. */
export const EXTERNAL_CONNECTORS = [
    'oracle',
    'sap',
    'Snowflake',
    'remote-postgres',
] as const;

export type ExternalConnector = (typeof EXTERNAL_CONNECTORS)[number];

/** Datasource `datatype` values for external sources (mirrors backend `DSExternalType`). */
export type ExternalDatatype = 'SAP' | 'Snowflake' | 'Oracle' | 'Remote Postgres';

/** `AddTablesComponent` dialog `type` values for external sources. */
export type ExternalTableDialogType =
    | 'fromSAP'
    | 'Snowflake'
    | 'fromOracle'
    | 'fromRemotePostgres';

interface ExternalConnectorSpec {
    /** Datasource `datatype`, and the `sourceType` the external-tables API expects. */
    datatype: ExternalDatatype;
    /** URL segment under `/data-sources/:id/external/`. */
    connector: ExternalConnector;
    /** `data1.type` passed into `AddTablesComponent`. */
    dialogType: ExternalTableDialogType;
}

/**
 * Single source of truth for the external connectors. Every datatype/connector/
 * dialog-type translation goes through this list so adding an engine is one entry
 * here rather than a new branch in each ternary across the feature.
 */
const EXTERNAL_CONNECTOR_SPECS: readonly ExternalConnectorSpec[] = [
    { datatype: 'SAP', connector: 'sap', dialogType: 'fromSAP' },
    { datatype: 'Snowflake', connector: 'Snowflake', dialogType: 'Snowflake' },
    { datatype: 'Oracle', connector: 'oracle', dialogType: 'fromOracle' },
    {
        datatype: 'Remote Postgres',
        connector: 'remote-postgres',
        dialogType: 'fromRemotePostgres',
    },
];

/** Dialog `type` values that mean "external table" in `AddTablesComponent`. */
export const EXTERNAL_TABLE_DIALOG_TYPES: readonly ExternalTableDialogType[] =
    EXTERNAL_CONNECTOR_SPECS.map(spec => spec.dialogType);

export interface DataSourceTablesRouteState {
    dataSourceId: string;
    /** External connector segment or internal deep-link table name. */
    isExternal: boolean;
    tableName: string;
}

function specByDatatype(datatype: string): ExternalConnectorSpec | undefined {
    return EXTERNAL_CONNECTOR_SPECS.find(spec => spec.datatype === datatype);
}

function specByConnector(connector: string): ExternalConnectorSpec | undefined {
    return EXTERNAL_CONNECTOR_SPECS.find(spec => spec.connector === connector);
}

/**
 * Maps datasource datatype to the external URL connector segment.
 * Unknown datatypes keep the historical `oracle` fallback.
 */
export function externalConnectorFromDatatype(datatype: string): ExternalConnector {
    return specByDatatype(datatype)?.connector ?? 'oracle';
}

/** Maps an external URL connector segment back to its datasource datatype. */
export function externalDatatypeFromConnector(connector: string): ExternalDatatype {
    return specByConnector(connector)?.datatype ?? 'SAP';
}

/** Maps an external URL connector segment to the `AddTablesComponent` dialog type. */
export function externalDialogTypeFromConnector(connector: string): ExternalTableDialogType {
    return specByConnector(connector)?.dialogType ?? 'fromSAP';
}

/**
 * Maps an `AddTablesComponent` dialog type back to the `sourceType` the
 * external-tables API expects. `undefined` for internal dialog types.
 */
export function externalDatatypeFromDialogType(
    dialogType: string | null | undefined
): ExternalDatatype | undefined {
    return EXTERNAL_CONNECTOR_SPECS.find(spec => spec.dialogType === dialogType)
        ?.datatype;
}

/** Maps a datasource datatype to the `AddTablesComponent` dialog type, or null when internal. */
export function externalDialogTypeFromDatatype(
    datatype: string
): ExternalTableDialogType | null {
    return specByDatatype(datatype)?.dialogType ?? null;
}

export function isExternalConnectorSegment(segment: string): boolean {
    return !!specByConnector(segment);
}

export function isExternalDatatype(datatype: string): boolean {
    return !!specByDatatype(datatype);
}

export const AWS_S3_DATATYPE = 'AWS_S3';
export const GOOGLE_DRIVE_DATATYPE = 'GOOGLE_DRIVE';

/** Backend `MediaContainerProvider` wire values that route to the Files / Add File UI. */
export const MEDIA_CONTAINER_DATATYPES: readonly string[] = [
    AWS_S3_DATATYPE,
    GOOGLE_DRIVE_DATATYPE,
];

export function isMediaContainerDatatype(datatype: string | null | undefined): boolean {
    return datatype != null && MEDIA_CONTAINER_DATATYPES.includes(datatype);
}

/** External Media Container source (AWS S3, Google Drive). Uses backend enums, not display labels. */
export function isMediaContainerDataSource(ds: {
    type?: string | null;
    datatype?: string | null;
} | null | undefined): boolean {
    if (!ds) {
        return false;
    }
    if (ds.type != null && ds.type !== '' && ds.type !== 'external') {
        return false;
    }
    return isMediaContainerDatatype(ds.datatype);
}

export function dataSourceFilesCommands(eid: string | number): (string | number)[] {
    return ['/data-sources', eid, 'files'];
}

export interface DataSourceOpenTarget {
    eid?: string | number | null;
    id?: string | number | null;
    type?: string | null;
    datatype?: string | null;
}

export function dataSourcePublicEid(
    ds: DataSourceOpenTarget | null | undefined,
): string | number | null {
    const eid = ds?.eid ?? ds?.id;
    if (eid == null || eid === '') {
        return null;
    }
    return eid;
}

/**
 * Single open-path for a DataSource from list/card/row/helper clicks.
 * AWS_S3 Media Container sources go to `/data-sources/:eid/files`, never tables.
 */
export function dataSourceOpenCommands(
    ds: DataSourceOpenTarget | null | undefined,
    options?: { allowMediaContainer?: boolean },
): (string | number)[] | null {
    const eid = dataSourcePublicEid(ds);
    if (eid == null || !ds) {
        return null;
    }
    if (isMediaContainerDataSource(ds)) {
        if (options?.allowMediaContainer === false) {
            return null;
        }
        return dataSourceFilesCommands(eid);
    }
    if (isExternalDatatype(String(ds.datatype ?? ''))) {
        return dataSourceExternalTablesCommands(eid, String(ds.datatype));
    }
    if (ds.type === 'internal' && ds.datatype === 'Unstructured Documents') {
        return ['/data-sources', 'document', eid];
    }
    return dataSourceTablesListCommands(eid);
}

/** True when an `AddTablesComponent` dialog type refers to an external table. */
export function isExternalTableDialogType(type: string | null | undefined): boolean {
    return (
        !!type && EXTERNAL_TABLE_DIALOG_TYPES.includes(type as ExternalTableDialogType)
    );
}

export function dataSourceTablesListCommands(
    eid: string | number
): (string | number)[] {
    return ['/data-sources', eid, 'tables'];
}

export function dataSourceTablesListPath(eid: string | number): string {
    return `/data-sources/${eid}/tables`;
}

export function dataSourceInternalTableCommands(
    eid: string | number,
    tableName: string
): (string | number)[] {
    return ['/data-sources', eid, 'tables', tableName];
}

export function dataSourceExternalTablesCommands(
    eid: string | number,
    datatype: string
): (string | number)[] {
    return [
        '/data-sources',
        eid,
        'external',
        externalConnectorFromDatatype(datatype),
    ];
}

/**
 * Parses tables-route params for canonical and legacy shapes.
 * Legacy `:tableId`/`isExternal` segments are still accepted until redirect runs.
 */
export function parseDataSourceTablesRouteParams(params: {
    id?: string;
    tableName?: string;
    connector?: string;
    tableId?: string;
    isExternal?: string;
}): DataSourceTablesRouteState {
    const dataSourceId = String(params.id ?? '');

    if (params.connector) {
        return {
            dataSourceId,
            isExternal: true,
            tableName: params.connector,
        };
    }

    if (params.isExternal === 'isExternal' && params.tableName) {
        return {
            dataSourceId,
            isExternal: true,
            tableName: params.tableName,
        };
    }

    if (params.tableName && isExternalConnectorSegment(params.tableName)) {
        return {
            dataSourceId,
            isExternal: true,
            tableName: params.tableName,
        };
    }

    return {
        dataSourceId,
        isExternal: false,
        tableName: params.tableName ?? '',
    };
}

/** Regex alternation of every connector segment, for the legacy URL rewrites. */
export const EXTERNAL_CONNECTOR_PATTERN = EXTERNAL_CONNECTORS.join('|');

/**
 * Rewrites legacy table URLs to canonical paths. Returns `null` when unchanged.
 *
 * Legacy:
 * - `/data-sources/:id/1` → `/data-sources/:id/tables`
 * - `/data-sources/:id/1/:tableName` → `/data-sources/:id/tables/:tableName`
 * - `/data-sources/:id/1/:connector/isExternal` → `/data-sources/:id/external/:connector`
 */
export function rewriteLegacyDataSourceTablesPath(path: string): string | null {
    const externalMatch = path.match(
        new RegExp(
            `^\\/data-sources\\/([^/]+)\\/1\\/(${EXTERNAL_CONNECTOR_PATTERN})\\/isExternal$`
        )
    );
    if (externalMatch) {
        return `/data-sources/${externalMatch[1]}/external/${externalMatch[2]}`;
    }

    const tableMatch = path.match(/^\/data-sources\/([^/]+)\/1\/([^/]+)$/);
    if (tableMatch) {
        return `/data-sources/${tableMatch[1]}/tables/${tableMatch[2]}`;
    }

    const listMatch = path.match(/^\/data-sources\/([^/]+)\/1$/);
    if (listMatch) {
        return `/data-sources/${listMatch[1]}/tables`;
    }

    return null;
}
